import { sanitizeUrl } from '@braintree/sanitize-url';
import { scaleOpacity } from '@dynamic/ui';
import { dequal } from 'dequal';
import ms from 'ms';

import { AlternativePurchaseParam } from '#/pages/AlternativePurchase/AlternativePurchasePage.types';

import { FontFamily, FontWeight, TextClass } from './config/common.types';
import { GradientDirection } from './constants';
import { GradientColors } from './theme/colors';

/** Returns a promise that will resolve after `ms` milliseconds. */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

/** Empty function. */
// we don't need `args`, but we need to allow callers to pass anything.
export const noop = (..._args: unknown[]): void => {
  // this comment prevents Sonar from treating this empty function as a critical issue
};

/** Utility for logging otherwise ignored errors.
 * In cases when we want to ignore a promise rejection, instead of catch(noop), or catch(console.error), use catch(ignoreError).
 * This is a noop with logging, that could be safely mocked out in tests (unlike console.error).
 */
export const ignoreError = (error: unknown): void => {
  console.error(error);
};

/** Empty function returning promise. */
// we don't need `args`, but we need to allow callers to pass anything.
export const noopPromise = (..._args: unknown[]): Promise<any> =>
  Promise.resolve();

/** Identity function. */
export const identity = <T>(arg: T): T => arg;

/** Limits `value` between `min` and `max`. */
export const limit = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Get the list of object keys that have truthy values. */
export const truthyKeys = (obj: object) =>
  Object.entries(obj)
    .filter(([, value]) => value)
    .map(([key]) => key);

/**
 * Turn falsy values to null, without Sonar complaining about it.
 * @example
 * x || 1 // Turns all falsy values to 1, but Sonar complains about it.
 * x ?? 1 // Turns only nullish values to 1, so it's not exactly the same. Use this IF it's safe.
 * falsyToNull(x) ?? 1 // Now it's the same as `x || 1`, and Sonar doesn't complain.
 */
export const falsyToNull = (value: any) => {
  // We can use this function everywhere else, but here we have to use `||` directly.
  return value || null; // NOSONAR
};

export enum SearchParams {
  OrgHash = 'orgHash',
  VariationHash = 'variationHash',
  Env = 'env',
  UiVersion = 'uiVersion',
  UiVersionBackend = 'ui-version',
  AppId = 'appId',
  Version = 'version',
  Platform = 'platform',
  Lang = 'lang',
  Local = 'local',
  CountryCode = 'countryCode',
  RegionCode = 'regionCode',
  VendorTag = 'vendorTag',
  IntegrationVersion = 'integrationVersion',
  CampaignTag = 'campaignTag',
}

export const searchParamsMap = Object.fromEntries(
  Object.values(SearchParams).map((value) => [value.toLowerCase(), value])
);

export enum SubscriptionParams {
  Product = 'product',
  Voucher = 'voucher',
}

/** Keep the technical search params, remove the rest, optionally add extras */
export const techSearchParams = (search: string, addExtra: object = {}) => {
  const keep = [
    SearchParams.OrgHash,
    SearchParams.VariationHash,
    SearchParams.Env,
    SearchParams.UiVersion,
    SearchParams.UiVersionBackend,
    SearchParams.AppId,
    SearchParams.Version,
    SearchParams.Platform,
    SearchParams.Lang,
    SearchParams.Local,
    SearchParams.CountryCode,
    SearchParams.RegionCode,
    SearchParams.VendorTag,
    SearchParams.IntegrationVersion,
    SearchParams.CampaignTag,
    SubscriptionParams.Product,
    SubscriptionParams.Voucher,
    AlternativePurchaseParam.Token,
    AlternativePurchaseParam.BundleId,
  ];
  const params = new URLSearchParams(search);
  Array.from(params.keys()).forEach((key) => {
    if (!keep.includes(key)) {
      params.delete(key);
    }
  });
  Object.entries(addExtra).forEach(([key, value]) => {
    params.set(key, value);
  });

  return params.toString();
};

/** Converts `px` to `rem` */
export const remify = (px: number, base = 18) => {
  return `${px / base}rem`;
};

const FWMap: FontWeight = {
  bold: 'bold',
  regular: 'normal',
};

const FFMap: FontFamily = {
  heading: 'HeadingFont',
  paragraph: 'ParagraphFont',
};

export const getFontStyle = (textClass?: TextClass) => {
  if (!textClass) {
    return '';
  }
  const { size, style, type, weight, lineHeight } = textClass;

  return `${style} normal ${FWMap[weight]} ${remify(size)}/${lineHeight} ${
    FFMap[type]
  }`;
};

export interface DeferredPromise<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
}

// promise that can be resolved/rejected from the outside
export const getDeferredPromise = <T>(): DeferredPromise<T> => {
  // initial values just to calm down TS
  let resolve: (value: T | PromiseLike<T>) => void = noop;
  let reject: (reason?: unknown) => void = noop;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return {
    promise,
    resolve,
    reject,
  };
};

/**
 * Sanitizes a URL before using it. Should always be used before rendering URLs from an external source, including config.
 * @param url {string} URL to sanitize.
 * @returns {string} Safe URL.
 */
export const safeUrl = (url: string) => sanitizeUrl(decodeURIComponent(url));

/**
 * Changes parameters in an absolute or relative URL.
 * @param url {string} Absolute or relative URL.
 * @param params {object} Object containing arbitrary parameters.
 * @returns {string} URL with changed parameters.
 */
export const changeUrlParams = (url: string, params: object) => {
  const [endpoint, search = ''] = url.split('?');
  const searchParams = new URLSearchParams(search);

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, String(value));
    }
  });

  return `${endpoint}?${searchParams.toString()}`;
};

/**
 * Creates a value cache. It is NOT for caching the results of expensive operations. It is for remembering the previous
 * resuls, doing the operation again, and if the two results are deeply equal, returning the previous one.
 * Example usecase: we have to do the menu item filtering on every context change, because conditional menu items might
 * change. But if they haven't, we can use the old results, preventing an unnecessary render.
 * Make sure you don't keep growing it with new values.
 * @returns {(cacheKey: string, value: T) => T} Value cacher function.
 */
export const createValueCacher = <T>(): ((cacheKey: string, value: T) => T) => {
  const cache: Record<string, T> = {};

  return (cacheKey: string, value: T) => {
    if (dequal(cache[cacheKey], value)) {
      return cache[cacheKey];
    }
    cache[cacheKey] = value;

    return value;
  };
};

/**
 * Creates a duplicate-free version of an array. ES6 replacement for `lodash.uniqBy`
 * @param arr The array to inspect
 * @param iteratee The iteratee invoked per element
 * @returns A new array without duplicates
 */
export function uniqBy<T>(arr: T[], iteratee: (el: T) => any) {
  return arr.filter(
    (x, i, self) => i === self.findIndex((y) => iteratee(x) === iteratee(y))
  );
}

/**
 * Transforms interval string used by Core into milliseconds
 * @example '15s' -> 15000
 */
export const coreIntervalToMilliseconds = (interval?: string): number => {
  if (!interval) {
    return 0;
  }

  return ms(interval);
};

/**
 * Convert snake_case and kebab-case to camelCase
 * @param text Text to convert
 * @returns 'live_channel' -> 'liveChannel' || 'live-channel' -> 'liveChannel'|| ''
 */
export const snakeToCamel = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[-_][a-z0-9]/g, (word) => word.slice(-1).toUpperCase());
};

/**
 * Convert a string to pascal case
 * @param text Text to convert
 * @returns 'primaryBackground' -> 'PrimaryBackground' || ''
 * @description Be aware that you might need to modify this function to your specific needs but hopefully it will cover most cases.
 */
export const pascal = (text: string) => {
  const regex =
    /[A-Z\xC0-\xD6\xD8-\xDE]?[a-z\xDF-\xF6\xF8-\xFF]+|[A-Z\xC0-\xD6\xD8-\xDE]+(?![a-z\xDF-\xF6\xF8-\xFF])|\d+/g;

  return (text.match(regex) || [])
    .map((word: string) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

/**
 * Creates a linear gradient CSS variable for buttons
 **/
export const getLinearGradient = (
  cssVariable: string,
  colors: GradientColors,
  direction: GradientDirection,
  state: 'Hover' | 'Disabled' | '' = ''
) => {
  const opacities = { Hover: 0.7, Disabled: 0.3, '': 1 };
  const opacity = opacities[state] ?? 1;
  const colorStops = colors
    .map(({ color, position }) => {
      const stopPosition = position ? ` ${position}%` : '';
      const stopColor = scaleOpacity(color, opacity);

      return `${stopColor}${stopPosition}`;
    })
    .join(',');

  return `--${cssVariable}LinearGradient${state}: linear-gradient(to ${direction},${colorStops});`;
};

export const isEmptyObject = (obj: object): boolean =>
  obj && Object.keys(obj).length === 0;

/**
 * Persisting query params in local or session storage.
 *
 * For example this util is needed to maintain campaign tags for MPP after PayPal redirect.
 * @param queryParam Query parameter key to look for.
 * @param storage Storage to use (local or session storage).
 * @returns The value of the query parameter.
 */
export const getQueryParamFromStorage = (
  queryParam: SearchParams,
  storage: Storage
) => {
  const queryParamValue = new URLSearchParams(window.location.search).get(
    queryParam
  );

  // If the query param is present in the URL, we need to store it in our storage and return it.
  if (queryParamValue) {
    try {
      storage.setItem(queryParam, queryParamValue);
    } catch (err) {
      console.error('Failed to set query param in storage', err);
    }

    return queryParamValue;
  }

  // If the query param isn't present in the URL, we need to get it from our storage and return it.
  return storage.getItem(queryParam) ?? undefined;
};

/**
 * A utility function to assert that a value is of type `never`.
 * This is useful to ensure that all cases are handled in a switch statement or similar control flow.
 * @param value Doesn't really matter, but it will be included in the error message if the assertion fails.
 * @returns The same value.
 */
export function assertNever(value: never): never {
  return value;
}

/**
 * Ensure that the query parameters in the URL are in a correct format.
 *
 * This function will normalize the query parameters by converting them to lowercase
 * and replacing any invalid keys with their canonical versions defined in `searchParamsMap`.
 * If a key is not found in the map, it will be left unchanged.
 */
export const ensureCorrectSearchParams = () => {
  const originalSearchParams = new URLSearchParams(window.location.search);
  const normalizedSearchParams = new URLSearchParams();

  originalSearchParams.forEach((value, key) => {
    // find the canonical key for the current search key.
    const canonicalKey = searchParamsMap[key.toLowerCase()];

    // if the search key have a valid format, we only append it to the normalized search params.
    if (!canonicalKey || canonicalKey === key) {
      normalizedSearchParams.set(key, value);

      return;
    }

    // if the search key have a invalid format, we append the canonical key to the normalized search params.
    normalizedSearchParams.set(canonicalKey, value);
  });

  const originalSearchString = originalSearchParams.toString();
  const normalizedSearchString = normalizedSearchParams.toString();

  // if the search params hasn't changed we don't need to update the URL.
  if (originalSearchString === normalizedSearchString) {
    return;
  }

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}?${normalizedSearchString}`
  );
};
